import { Component, inject, OnInit } from '@angular/core';
import { Tag } from 'primeng/tag';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { relativeTime } from '@/app/utils/relative-time';
import {
    AiAssistService,
    AI_ASSIST_ICON,
} from '@/app/services/ai-assist.service';

const JOBS_LIMIT = 10;

@Component({
    standalone: true,
    selector: 'app-ai-assist-jobs-widget',
    imports: [Tag, Button, Tooltip],
    template: `
        <div class="card flex flex-col gap-2">
            <div class="flex items-center justify-between mb-2">
                <span class="font-semibold text-lg flex items-center gap-2">
                    <i [class]="aiIcon"></i>
                    My AI Assist jobs
                </span>
                <div class="flex items-center gap-2">
                    @if (aiAssist.jobsActive() > 0) {
                        <p-tag
                            [value]="aiAssist.jobsActive() + ' active'"
                            severity="info"
                        />
                    }
                    <p-button
                        icon="pi pi-refresh"
                        severity="secondary"
                        [text]="true"
                        [rounded]="true"
                        pTooltip="Refresh"
                        (click)="reload()"
                    />
                </div>
            </div>

            @if (aiAssist.jobs().length === 0) {
                <span class="text-sm text-muted-color">
                    {{
                        aiAssist.jobsLoaded()
                            ? 'No recent jobs.'
                            : 'Could not load your jobs.'
                    }}
                </span>
            } @else {
                <ul
                    class="flex flex-col divide-y divide-surface-200 dark:divide-surface-700 m-0 p-0 list-none"
                >
                    @for (job of aiAssist.jobs(); track job.job_id) {
                        <li class="py-3 flex flex-col gap-1">
                            <div
                                class="flex items-center justify-between gap-3"
                            >
                                <div class="flex items-center gap-2 min-w-0">
                                    <span class="font-medium">{{
                                        jobKindLabel(job.kind)
                                    }}</span>
                                    <span
                                        class="text-xs text-muted-color whitespace-nowrap"
                                        [pTooltip]="
                                            absoluteDate(job.created_at)
                                        "
                                        tooltipPosition="top"
                                        >{{ relative(job.created_at) }}</span
                                    >
                                </div>
                                <p-tag
                                    [value]="job.status"
                                    [severity]="jobStatusSeverity(job.status)"
                                />
                            </div>
                            @if (job.status === 'failed' && job.error) {
                                <span class="text-xs text-red-500">{{
                                    job.error.message || job.error.code
                                }}</span>
                            }
                        </li>
                    }
                </ul>
            }
        </div>
    `,
})
export class AiAssistJobsWidget implements OnInit {
    protected readonly aiAssist = inject(AiAssistService);
    protected readonly aiIcon = AI_ASSIST_ICON;

    ngOnInit(): void {
        this.reload();
    }

    protected reload(): void {
        void this.aiAssist.refreshMyJobs({ limit: JOBS_LIMIT });
    }

    protected jobKindLabel(kind: string): string {
        switch (kind) {
            case 'base_art':
                return 'Base art';
            case 'asset_finalize':
                return 'Finalize';
            case 'pages':
                return 'Pages';
            default:
                return kind;
        }
    }

    protected jobStatusSeverity(
        status: string,
    ): 'success' | 'info' | 'danger' | 'secondary' {
        switch (status) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'info';
            case 'failed':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    protected relative(when: string): string {
        return relativeTime(when);
    }

    protected absoluteDate(when: string): string {
        return new Date(when).toLocaleString();
    }
}
